import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Inventory,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface InventoryItem {
  id: string;
  nume: string;
  categorie: string;
  cantitate: number;
  unitate: string;
  valoare: number;
  parohieId?: string;
  gestiuneId: string;
  gestiuneNume?: string;
}

const Inventar: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');
  const [filterGestiune, setFilterGestiune] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'table'>('table');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  // Mock data pentru inventar
  const [inventory] = useState<InventoryItem[]>([
    {
      id: '1',
      nume: 'Candelabru de argint',
      categorie: 'Obiecte de cult',
      cantitate: 2,
      unitate: 'buc',
      valoare: 1500,
      parohieId: 'parohia1',
      gestiuneId: 'gest1',
      gestiuneNume: 'Magazia Principală'
    },
    {
      id: '2',
      nume: 'Cărți religioase',
      categorie: 'Biblioteca',
      cantitate: 150,
      unitate: 'buc',
      valoare: 3000,
      parohieId: 'parohia1',
      gestiuneId: 'gest2',
      gestiuneNume: 'Sacristia'
    },
    {
      id: '3',
      nume: 'Mobilier bisericesc',
      categorie: 'Mobilier',
      cantitate: 10,
      unitate: 'buc',
      valoare: 5000,
      parohieId: 'parohia2',
      gestiuneId: 'gest3',
      gestiuneNume: 'Depozitul Central'
    },
    {
      id: '4',
      nume: 'Veșminte liturgice',
      categorie: 'Obiecte de cult',
      cantitate: 5,
      unitate: 'set',
      valoare: 2500,
      parohieId: 'parohia1',
      gestiuneId: 'gest2',
      gestiuneNume: 'Sacristia'
    },
    {
      id: '5',
      nume: 'Materiale de construcție',
      categorie: 'Tehnică',
      cantitate: 100,
      unitate: 'kg',
      valoare: 1200,
      parohieId: 'parohia2',
      gestiuneId: 'gest3',
      gestiuneNume: 'Depozitul Central'
    }
  ]);

  const inventoryCategories = ['Obiecte de cult', 'Biblioteca', 'Mobilier', 'Tehnică', 'Altele'];

  // Mock data pentru gestiuni
  const gestiuni = [
    { id: 'gest1', nume: 'Magazia Principală' },
    { id: 'gest2', nume: 'Sacristia' },
    { id: 'gest3', nume: 'Depozitul Central' }
  ];

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.nume.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.categorie.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.gestiuneNume?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategorie || item.categorie === filterCategorie;
    const matchesGestiune = !filterGestiune || item.gestiuneId === filterGestiune;
    
    return matchesSearch && matchesCategory && matchesGestiune;
  });

  const totalValoare = inventory.reduce((sum, item) => sum + item.valoare, 0);
  const totalItems = inventory.reduce((sum, item) => sum + item.cantitate, 0);
  const categoriiUnice = Array.from(new Set(inventory.map(item => item.categorie))).length;
  const gestiuniUnice = Array.from(new Set(inventory.map(item => item.gestiuneId))).length;

  const handleViewItem = (item: InventoryItem) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Inventar
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('table')}
          >
            Tabel
          </Button>
          <Button
            variant={viewMode === 'list' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('list')}
          >
            Listă
          </Button>
          {hasPermission('gestiune.write') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {/* Implementare adăugare item nou */}}
            >
              Item nou
            </Button>
          )}
        </Box>
      </Box>

      {/* Statistici inventar */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  📦
                </Typography>
                <Typography color="textSecondary">
                  Total itemuri
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                {totalItems}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  🏷️
                </Typography>
                <Typography color="textSecondary">
                  Categorii
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                {categoriiUnice}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  🏢
                </Typography>
                <Typography color="textSecondary">
                  Gestiuni
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {gestiuniUnice}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  💰
                </Typography>
                <Typography color="textSecondary">
                  Valoare totală
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalValoare.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru inventar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Caută în inventar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Categorie</InputLabel>
              <Select
                value={filterCategorie}
                onChange={(e) => setFilterCategorie(e.target.value)}
                label="Categorie"
              >
                <MenuItem value="">Toate</MenuItem>
                {inventoryCategories.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Gestiune</InputLabel>
              <Select
                value={filterGestiune}
                onChange={(e) => setFilterGestiune(e.target.value)}
                label="Gestiune"
              >
                <MenuItem value="">Toate</MenuItem>
                {gestiuni.map(gestiune => (
                  <MenuItem key={gestiune.id} value={gestiune.id}>{gestiune.nume}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterCategorie('');
                setFilterGestiune('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Vizualizare tabel */}
      {viewMode === 'table' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell>Gestiune</TableCell>
                <TableCell>Cantitate</TableCell>
                <TableCell>Valoare unitară</TableCell>
                <TableCell>Valoare totală</TableCell>
                <TableCell>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInventory.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Inventory sx={{ mr: 2, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {item.nume}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.parohieId}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.categorie}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.gestiuneNume}
                      color="secondary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.cantitate} {item.unitate}
                    </Typography>
                  </TableCell>
                  <TableCell>€{(item.valoare / item.cantitate).toLocaleString()}</TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">
                      €{item.valoare.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleViewItem(item)}
                      color="primary"
                    >
                      <ViewIcon />
                    </IconButton>
                    {hasPermission('gestiune.write') && (
                      <IconButton color="secondary">
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Vizualizare listă */}
      {viewMode === 'list' && (
        <List>
          {filteredInventory.map((item) => (
            <React.Fragment key={item.id}>
              <ListItem>
                <ListItemIcon>
                  <Inventory />
                </ListItemIcon>
                <ListItemText
                  primary={item.nume}
                  secondary={`${item.cantitate} ${item.unitate} • ${item.categorie} • ${item.gestiuneNume}`}
                />
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" color="primary">
                    €{item.valoare.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Valoare totală
                  </Typography>
                </Box>
                <Box sx={{ ml: 2 }}>
                  <IconButton
                    onClick={() => handleViewItem(item)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {hasPermission('gestiune.write') && (
                    <IconButton color="secondary">
                      <EditIcon />
                    </IconButton>
                  )}
                </Box>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Dialog pentru vizualizare item */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii item inventar
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Inventory sx={{ mr: 2, color: 'primary.main', fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {selectedItem.nume}
                    </Typography>
                    <Chip
                      label={selectedItem.categorie}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Cantitate
                </Typography>
                <Typography variant="h6">
                  {selectedItem.cantitate} {selectedItem.unitate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Valoare unitară
                </Typography>
                <Typography variant="h6">
                  €{(selectedItem.valoare / selectedItem.cantitate).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Valoare totală
                </Typography>
                <Typography variant="h5" color="primary.main">
                  €{selectedItem.valoare.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Gestiune
                </Typography>
                <Typography variant="body1">
                  {selectedItem.gestiuneNume || 'Necunoscută'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Parohie
                </Typography>
                <Typography variant="body1">
                  {selectedItem.parohieId || 'Toate parohiile'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          {hasPermission('gestiune.write') && (
            <Button variant="contained" startIcon={<EditIcon />}>
              Editează
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventar;
